import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react'
import type {WSEvent} from '@interfaces/alpinist/ws'
import {
  WS_CLOSE_FORBIDDEN,
  isServiceSocketAuthError,
  openServiceSocket,
} from '@shared/websocket/serviceSocket'

type PayloadOf<K extends WSEvent['kind']> = Extract<WSEvent, {kind: K}>['payload']

type Handlers = {
  onMessage?: (evt: WSEvent) => void
  onToolUpdate?: (payload: PayloadOf<'tool.update'>) => void
  onAttachmentIndexing?: (payload: PayloadOf<'attachment.indexing'>) => void
  onAgentToken?: (payload: PayloadOf<'agent.token'>) => void
  onMsgFinal?: (payload: PayloadOf<'msg.final'>) => void
  onTitleStarted?: (payload: PayloadOf<'conv.title.started'>) => void
  onTitleUpdated?: (payload: PayloadOf<'conv.title.updated'>) => void
  onCacheInvalidate?: (payload: PayloadOf<'cache.invalidate'>) => void
  onError?: (err: string) => void
}

type Ctx = {
  subscribe: (h: Handlers) => () => void
  connected: boolean
  send: (payload: unknown) => void
  reconnectNow: () => void
}

const ConversationStreamCtx = createContext<Ctx | null>(null)

type Props = {conversationId: string | null | undefined; children: React.ReactNode}

const noop = (..._args: unknown[]): void => {}

const toErrMsg = (e: unknown): string => {
  if (typeof e === 'string') {
    return e
  }
  if (e && typeof e === 'object' && 'message' in e) {
    const msg = (e as {message?: unknown}).message
    if (typeof msg === 'string') {
      return msg
    }
    try {
      return JSON.stringify(msg)
    } catch {
      return String(msg)
    }
  }
  try {
    return JSON.stringify(e)
  } catch {
    return String(e)
  }
}

export const ConversationStreamProvider: React.FC<Props> = ({conversationId, children}) => {
  const [connected, setConnected] = useState(false)

  const wsRef = useRef<WebSocket | null>(null)
  const pingTimer = useRef<number | null>(null)
  const reconnectTimer = useRef<number | null>(null)

  const subsRef = useRef<Set<Handlers>>(new Set())
  const manualCloseRef = useRef(false) // set true when we intentionally tear down
  const attemptRef = useRef(0) // reconnect attempt count
  // Every connect fetches a single-use ticket first (AB#6423). `connectingRef`
  // blocks a second connect while one is in flight; `generationRef` is bumped
  // on every open/teardown so a ticket that resolves late is discarded.
  const connectingRef = useRef(false)
  const generationRef = useRef(0)

  const subscribe = useCallback<Ctx['subscribe']>((handlers) => {
    const h = handlers ?? {}
    subsRef.current.add(h)
    return () => {
      subsRef.current.delete(h)
      return undefined
    }
  }, [])

  const notify = useCallback((fn: (h: Handlers) => void) => {
    for (const h of Array.from(subsRef.current)) {
      try {
        fn(h)
      } catch {
        /* no-op */
      }
    }
  }, [])

  // --- helpers ---------------------------------------------------------------

  const clearTimers = useCallback(() => {
    if (pingTimer.current) {
      clearInterval(pingTimer.current)
      pingTimer.current = null
    }
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current)
      reconnectTimer.current = null
    }
  }, [])

  const closeSocket = useCallback(() => {
    generationRef.current += 1
    connectingRef.current = false
    try {
      wsRef.current?.close()
    } catch (err) {
      noop(err)
    }
    wsRef.current = null
    setConnected(false)
    clearTimers()
  }, [clearTimers])

  const scheduleReconnect = useCallback(() => {
    if (!conversationId) return
    if (manualCloseRef.current) return // don’t reconnect during teardown

    // exponential backoff with a bit of jitter
    const base = 1000 // 1s
    const max = 10000 // 10s
    const attempt = attemptRef.current
    const delay = Math.min(base * 2 ** attempt, max) + Math.floor(Math.random() * 250)

    // console.debug(`[WS] reconnect in ${delay}ms (${why})`)
    if (reconnectTimer.current) clearTimeout(reconnectTimer.current)
    reconnectTimer.current = window.setTimeout(() => {
      openSocket()
    }, delay) as unknown as number

    attemptRef.current = attempt + 1
  }, [conversationId])

  const openSocket = useCallback(async () => {
    if (!conversationId) return
    if (connectingRef.current) return // a ticket fetch is already in flight

    // If there’s an existing socket, close it first (defensively)
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.close()
      } catch (err) {
        noop(err)
      }
    }
    wsRef.current = null
    clearTimers()

    manualCloseRef.current = false
    generationRef.current += 1
    const generation = generationRef.current
    connectingRef.current = true

    let ws: WebSocket
    try {
      ws = await openServiceSocket('aicore', `/aicore/ws/conversations/${conversationId}/`)
    } catch (error) {
      if (generation !== generationRef.current) return // torn down meanwhile
      connectingRef.current = false
      // A 401 already went through logout; anything else retries with backoff.
      if (!manualCloseRef.current && !isServiceSocketAuthError(error) && navigator.onLine) {
        scheduleReconnect()
      }
      return
    }
    if (generation !== generationRef.current || manualCloseRef.current) {
      ws.close() // unmounted, conversation changed or superseded mid-fetch
      return
    }
    connectingRef.current = false
    wsRef.current = ws

    ws.onopen = () => {
      setConnected(true)
      attemptRef.current = 0 // reset backoff on success

      // heartbeat ping to keep idle connections alive (if server allows)
      pingTimer.current = window.setInterval(() => {
        if (ws.readyState !== WebSocket.OPEN) return
        try {
          ws.send(JSON.stringify({ping: Date.now()}))
        } catch {
          // if send fails, force a reconnect cycle
          try {
            ws.close()
          } catch (err) {
            noop(err)
          }
        }
      }, 20000) as unknown as number
    }

    ws.onmessage = (evt) => {
      let msg: WSEvent
      try {
        msg = JSON.parse(evt.data) as WSEvent
      } catch (e) {
        notify((h) => h.onError?.(toErrMsg(e)))
        return
      }

      notify((h) => h.onMessage?.(msg))

      switch (msg.kind) {
        case 'tool.update':
          notify((h) => h.onToolUpdate?.(msg.payload))
          break
        case 'attachment.indexing':
          notify((h) => h.onAttachmentIndexing?.(msg.payload))
          break
        case 'agent.token':
          notify((h) => h.onAgentToken?.(msg.payload))
          break
        case 'msg.final':
          notify((h) => h.onMsgFinal?.(msg.payload))
          break
        case 'conv.title.started':
          notify((h) => h.onTitleStarted?.(msg.payload))
          break
        case 'conv.title.updated':
          notify((h) => h.onTitleUpdated?.(msg.payload))
          break
        case 'cache.invalidate':
          notify((h) => h.onCacheInvalidate?.(msg.payload))
          break
        default:
          break
      }
    }

    ws.onerror = () => {
      if (wsRef.current !== ws) return // stale socket
      setConnected(false)
      // allow onclose handler to schedule reconnect
    }

    ws.onclose = (e) => {
      // A replaced socket closing late must not tear down its successor.
      if (wsRef.current !== ws) return
      setConnected(false)
      clearTimers()

      // Don’t reconnect if we’re intentionally closing (unmount/change)
      if (manualCloseRef.current) return

      // Some close codes indicate we should *not* reconnect automatically.
      // 1000 = normal close; 4403 = forbidden. 1001/1006 common for network/idle;
      // 4401 = session expired, reconnects on a fresh ticket like any other.
      const shouldReconnect = ![1000, WS_CLOSE_FORBIDDEN].includes(e.code)

      if (shouldReconnect && navigator.onLine) {
        scheduleReconnect()
      } else {
        // wait for network to come back / user interaction
      }
    }
  }, [clearTimers, notify, scheduleReconnect, conversationId])

  // --- lifecycle / wiring ----------------------------------------------------

  useEffect(() => {
    // tear down any previous socket/timers and reset flags
    manualCloseRef.current = true
    closeSocket()
    attemptRef.current = 0
    manualCloseRef.current = false

    if (!conversationId) return
    openSocket()

    // reconnect immediately when browser comes back online
    const onOnline = () => {
      if (wsRef.current?.readyState !== WebSocket.OPEN) {
        scheduleReconnect()
      }
    }

    // when tab becomes visible again after sleep, ensure we have a live socket
    const onVisible = () => {
      if (document.visibilityState === 'visible' && wsRef.current?.readyState !== WebSocket.OPEN) {
        scheduleReconnect()
      }
    }

    window.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      window.removeEventListener('online', onOnline)
      document.removeEventListener('visibilitychange', onVisible)
      manualCloseRef.current = true
      closeSocket()
    }
  }, [conversationId, closeSocket, openSocket, scheduleReconnect])

  const send: Ctx['send'] = useCallback((payload) => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    try {
      ws.send(JSON.stringify(payload))
    } catch {
      noop()
    }
  }, [])

  const reconnectNow = useCallback(() => {
    if (!conversationId) return
    // reset backoff, close any stale socket, and open a fresh one
    attemptRef.current = 0
    manualCloseRef.current = false
    try {
      wsRef.current?.close()
    } catch {
      noop()
    }
    openSocket()
  }, [conversationId, openSocket])

  const value: Ctx = {subscribe, connected, send, reconnectNow}
  return <ConversationStreamCtx.Provider value={value}>{children}</ConversationStreamCtx.Provider>
}

export function useConversationEvents(): Ctx {
  const ctx = useContext(ConversationStreamCtx)
  return (
    ctx ?? {
      subscribe: () => () => undefined,
      connected: false,
      send: () => undefined,
      reconnectNow: () => undefined,
    }
  )
}
